/* eslint-disable import/no-cycle */
import {getPercentageValue, isEmptyOrUndefined} from '../../../utils/common/validators';
import {
	AMO_DISABLED_PRODUCTS,
	DEFAULT_ORDER_FORM_DATA,
	DEFAULT_SIP_FORM_DATA,
	IOC_DISABLED_PRODUCTS,
	ORDER_BUTTON_TITLE,
	ORDER_TYPES,
	TRANSACTION_TYPE,
	VALIDITY_TYPE,
} from '../../../constants/equity/orderForm/oneTime';

import {getOneTimeIsAmo, setOneTimeData, setSipData} from '../../../dataStore/reducers/equity/orderForm';
import orderScripService from './orderScripService';
import orderFormValidationService from './orderFormValidationService';
import {getDefaultOrderFormTab, getDefaultOrderType} from '../../../dataStore/reducers/equity/equityGlobal';
import {max, noop} from 'lodash-es';
import {
	COUNTER_ACTIONS,
	EDIS_PRODUCTS,
	EQUITY_MARKET_SEGMENTS,
	MARKET_FLAG_MAPPER,
	PRODUCT_TYPES_LIST,
	PRODUCT_TYPES_MAPPER,
	SCRIP_INSTRUMENTS,
} from '../../../constants/equity/orderForm/orderFormBase';
import numberValidityService from './numberValidityService';
import {EQUITY_API_SEGMENTS, EQUITY_SEGMENT_IDS, EQUITY_SEGMENTS} from "../../../constants/equity/common";
import commonBaseService from '../../commonBaseService';

function isOrderTypeStoploss(orderType) {
	return orderType === ORDER_TYPES.stoploss.value;
}

function isOrderTypeStoplossLimit(orderType) {
	return orderType === ORDER_TYPES.stoplossMarket.value;
}

function checkShouldShowStoploss(selectedOrderType) {
	const isStoplossLimitSelected = isOrderTypeStoploss(selectedOrderType);
	const isStoplossMarketSelected = isOrderTypeStoplossLimit(selectedOrderType);
	return isStoplossLimitSelected || isStoplossMarketSelected;
}

function checkShouldShowDisclosedQuantity(validity) {
	return validity === VALIDITY_TYPE.IMMEDIATE.value;
}

function handleIncreamentDecrementAction(action, initialValue, changeBy = 1, supportedDecimals = 0) {
	let finalValue = initialValue;
	const { isDivisible, quotient } = orderFormValidationService.checkIsDivisible(initialValue, changeBy);

	if (action === COUNTER_ACTIONS.ADD) {
		if (isDivisible) {
			finalValue += changeBy;
		} else {
			const nextValidValue = Math.ceil(quotient) * changeBy;
			finalValue = nextValidValue;
		}
	} else if (action === COUNTER_ACTIONS.SUBTRACT) {
		if (isDivisible) {
			finalValue -= changeBy;
		} else {
			const previousValidValue = Math.floor(quotient) * changeBy;
			finalValue = previousValidValue;
		}
	} else {
		return initialValue;
	}

	if (supportedDecimals > 0) {
		return parseFloat(finalValue).toFixed(supportedDecimals);
	} else {
		return parseInt(finalValue, 10);
	}
}

function isMarketOrder(orderType) {
	return orderType === ORDER_TYPES.market.value;
}
function isCarryForwardProduct(productType) {
	return productType === PRODUCT_TYPES_MAPPER.carryforward.id;
}

function checkAmoDisabled(productType, orderType, instrumentName, validity) {
	const isAMORestrictred = AMO_DISABLED_PRODUCTS.includes(productType);
	const _isCarryForwardProduct = isCarryForwardProduct(productType);
	const isProductTypeOptions = orderScripService.isOptionScrip(instrumentName);
	const _isMarketOrder = isMarketOrder(orderType);
	const isOptionMarketOrder = _isCarryForwardProduct && _isMarketOrder && isProductTypeOptions;
	const _isIocOrder = isIocOrder(validity);

	return isAMORestrictred || isOptionMarketOrder || (_isIocOrder && !_isCarryForwardProduct);
}

function isIocDisabled(productType, isAmo) {
	const isIocRestricted = IOC_DISABLED_PRODUCTS.includes(productType);
	const _isCarryForwardProduct = isCarryForwardProduct(productType);

	return isIocRestricted || (isAmo && !_isCarryForwardProduct);
}

function isIocOrder(validity) {
	return validity === VALIDITY_TYPE.IMMEDIATE.value;
}


function isDecimalNumber(num) {
	if (isEmptyOrUndefined(num)) return false;
	const number = Number(num);
	if (Number.isNaN(number)) return false;
	const isInteger = Number.isInteger(Number(number));
	return !isInteger;
}

// this function returns array of appliable product types for a user
function getApplicableProductTypesForUser(
	instrumentName,
	availableProductTypes = [],
	transactionType = '',
	isModifySip = false,
	isModifyOrder = false,
	modifyProductType = '',
	isSquareOff = false,
) {
	let applicableProducts = [];
	if (isEmptyOrUndefined(instrumentName)) {
		return [];
	}

	if (isModifyOrder || isSquareOff) {
		const productType = PRODUCT_TYPES_MAPPER[modifyProductType?.toLowerCase()];
		applicableProducts = [productType];
		return applicableProducts;
	}

	if (isModifySip) {
		const availableProducts = [];
		availableProducts.push(PRODUCT_TYPES_MAPPER.sip.id);
		applicableProducts = PRODUCT_TYPES_LIST.filter((item) => {
			return availableProducts?.includes(item?.id);
		});
		return applicableProducts;
	}

	const isCarryForwardProduct = orderScripService.isCarryForwardProduct(instrumentName);
	if (isCarryForwardProduct) {
		applicableProducts.push(PRODUCT_TYPES_MAPPER.carryforward);
		return applicableProducts;
	}

	const availableProducts = [...availableProductTypes]; // ['DELIVERY' , 'INTRADAY']
	const isDeliveryProductApplicable = availableProductTypes.includes(PRODUCT_TYPES_MAPPER.delivery.id);
	const isSipApplicable = transactionType === TRANSACTION_TYPE.BUY && isDeliveryProductApplicable;

	if (isSipApplicable) {
		availableProducts.push(PRODUCT_TYPES_MAPPER.sip.id);
	}

	applicableProducts = PRODUCT_TYPES_LIST.filter((item) => {
		return availableProducts?.includes(item?.id);
	});

	return applicableProducts;
}

// test cases
function getEditOrderDisclosedQty(disclosedQtyVal, pendingQuantityVal) {
	const disclosedQty = Number(disclosedQtyVal);
	const pendingQuantity = Number(pendingQuantityVal);

	if (disclosedQty > pendingQuantity) {
		return 0;
	} else if (disclosedQty < pendingQuantity) {
		const percentageQty = Math.round(getPercentageValue(10, pendingQuantity));
		return Math.max(percentageQty, disclosedQty);
	} else {
		return disclosedQty;
	}
}

function isMarketOrStopLossMarketOrder(orderType) {
	return [ORDER_TYPES.market.value, ORDER_TYPES.stoplossMarket.value].includes(orderType);
}

function isLimitOrStopLossOrder(orderType) {
	return [ORDER_TYPES.limit.value, ORDER_TYPES.stoploss.value].includes(orderType);
}

// DESCRIPTION (orderFormRedirection) : initialFormData
// {
// 	isAmo,
// 	orderType,
// 	quantity,
// 	stoplossTrigger,
// 	exchange, // NSE or BSE
//  disclosedQuantity
// 	validity: validity,

// ---- below comes specifically in case of modify order -----
//  tradedQuantity: traded_quantity,
// 	orderId:  orderId,
// };

//  DESCRIPTION (orderFormRedirection) : param
// {
// 	scripToken,
// 	segmentId, // map it using API_SEGMENT_ID_MAPPER
// 	transactionType, // map using TRANSACTION_TYPES
// 	productType,
// 	isRepeateOrReorder,
//  isModifyOrder
// };

function orderFormRedirection({
  navigation,
  params = {},
  initialFormData = {},
  store,
  goToOrderForm = noop,
}) {
  if (!isEmptyOrUndefined(initialFormData)) {
    store.dispatch(setOneTimeData(initialFormData));
    store.dispatch(setSipData(initialFormData));
	params['shouldUseDefaultOrderType'] = commonBaseService.isEmptyOrUndefined(initialFormData?.orderType);
  } else {
    store.dispatch(setOneTimeData(DEFAULT_ORDER_FORM_DATA));
    store.dispatch(setSipData(DEFAULT_SIP_FORM_DATA));
  }

  goToOrderForm(navigation, params);
}

function getMarginMarketFlag(orderType) {
	const isMktOrStopLossMktOrder = isMarketOrStopLossMarketOrder(orderType);
	return isMktOrStopLossMktOrder ? MARKET_FLAG_MAPPER.MARKET_FLAG : MARKET_FLAG_MAPPER.LIMIT_FLAG;
}

function isSellTransaction(transactionType) {
	return String(transactionType).toUpperCase() === TRANSACTION_TYPE.SELL;
}

function isDeliveryOrder(productType) {
	return String(productType).toUpperCase() === PRODUCT_TYPES_MAPPER.delivery.id;
}

function isIntradayOrder(productType) {
	return String(productType).toUpperCase() === PRODUCT_TYPES_MAPPER.intraday.id;
}

function isDeliverySellOrder(transactionType, productType) {
	const isSellOrder = isSellTransaction(transactionType);
	const isDelivery = isDeliveryOrder(productType);
	return isSellOrder && isDelivery;
}

function isEquityOrderByApiSegment(apiSegmentId) {
	return EQUITY_API_SEGMENTS.includes(Number(apiSegmentId));
}

function isEquityOrderBySegmentID(segmentId) {
	return EQUITY_SEGMENT_IDS.includes(Number(segmentId));
}

function isEquityOrderByExchange(exchange) {
	return EQUITY_SEGMENTS.includes(exchange);
}

function isEdisApplicable(transactionType, productType, poaFlag) {
	const isSellOrder = isSellTransaction(transactionType);
	const isEdisApplicableProduct = EDIS_PRODUCTS.includes(productType);
	const isPoaApplicable = poaFlag === 2;
	return isSellOrder && isEdisApplicableProduct && isPoaApplicable;
}

function getCurrencySymbolForCDS(symbol) {
	return symbol ? symbol.slice(0, 3) : '';
}

function isEdisAndHoldingsApplicable(instrument) {
	return instrument === SCRIP_INSTRUMENTS.EQUITY;
}

function getOrderSwiperTitle({ isModifyOrder = false, isModifySip = false, transactionType = '', productType = '' }) {
	let journeyType = transactionType;

	if (isModifySip) {
		journeyType = TRANSACTION_TYPE.MODIFY_SIP;
	} else if (isModifyOrder && transactionType === TRANSACTION_TYPE.BUY) {
		journeyType = TRANSACTION_TYPE.MODIFY_BUY;
	} else if (isModifyOrder && transactionType === TRANSACTION_TYPE.SELL) {
		journeyType = TRANSACTION_TYPE.MODIFY_SELL;
	} else if (productType === 'SIP') {
		journeyType = TRANSACTION_TYPE.SIP;
	} else {
		journeyType = transactionType;
	}

	const title = ORDER_BUTTON_TITLE[journeyType];
	return title;
}

const isStopLossMarketOrder = (orderType) => {
	return orderType === ORDER_TYPES.stoplossMarket.value;
};

const getDefaultOrPreviousOrderType = (instrument, store) => {
	const state = store.getState();
	const isAmo = getOneTimeIsAmo(state);
	const previousOrderType = getDefaultOrderType(state);
	if (!commonBaseService.isEmptyOrUndefined(previousOrderType)) {
		const disableStopLossMarket = isStopLossMarketAndDisabled(
			instrument,
			previousOrderType,
		);
		const disableMarket = isMarketAndDisabled(instrument, previousOrderType, isAmo);
		const isPreviousOrderTypeApplicable = !disableMarket && !disableStopLossMarket;
		if (isPreviousOrderTypeApplicable) {
			return previousOrderType;
		}
	}
	const isCarryForwardProduct = orderScripService.isCarryForwardProduct(instrument);
	if (isCarryForwardProduct) {
		return ORDER_TYPES.limit.value;
	} else {
		return ORDER_TYPES.market.value;
	}
};

function getDefaultTab(scripType, isModifyOrder, isModifySip, isSquareOff, productType, store, isDeeplink = false) {
	if (isModifyOrder || isDeeplink) {
		return PRODUCT_TYPES_MAPPER[productType?.toLowerCase()];
	}

	if (isModifySip) {
		return PRODUCT_TYPES_MAPPER.sip;
	}

	if (isSquareOff) {
		return PRODUCT_TYPES_MAPPER[productType?.toLowerCase()];
	}

	// if scripType is not equity then delivery tab is default
	// else delivery tab is default if user is logged in else intraday tab is default.
	// if user is logged in and scripType is equity then delivery tab is default.
	// else intraday tab is default.
	// if user is logged in and scripType is not equity then delivery tab is default.
	// else delivery tab is default.
	// if user is not logged in and scripType is not equity then delivery tab is default.
	// else intraday tab is default.
	// if user is not logged in and scripType is equity then delivery tab is default.
	// else delivery tab is default.
	const isEquityScrip = orderScripService.isEquityScrip(scripType);
	if (isEquityScrip) {
		const state = store.getState();
		const defaultTab = getDefaultOrderFormTab(state);
		return isEmptyOrUndefined(defaultTab) ? PRODUCT_TYPES_MAPPER.delivery : defaultTab;
	}

	return PRODUCT_TYPES_MAPPER.carryforward;
}

function isDeliveryOrIntradayOrder(productType) {
	return [PRODUCT_TYPES_MAPPER.delivery.id, PRODUCT_TYPES_MAPPER.intraday.id].includes(productType);
}

function showAvailableQuantity(instrumentName, transactionType, isDeliveryOrderForm) {
	const isSellTransaction = transactionType === TRANSACTION_TYPE.SELL;
	return isEdisAndHoldingsApplicable(instrumentName) && isSellTransaction && isDeliveryOrderForm;
}

const getFormattedLotText = (fieldValue, quantity, lotSize, displaySymbol, isCurrencyScrip) => {
	const div = fieldValue / lotSize;
	const notDivsible = isDecimalNumber(div);
	const minFieldValue = isCurrencyScrip ?  1 : lotSize;
	const shouldHideLotCalculation = fieldValue < minFieldValue;

	if (shouldHideLotCalculation) {
		return '';
	} else if (isCurrencyScrip && fieldValue) {
		return `${quantity} ${quantity > 1 ? 'lots' : 'lot'} x ${lotSize}${displaySymbol}`;
	} else if (notDivsible || isEmptyOrUndefined(fieldValue)) {
		return `- lot x ${lotSize}${displaySymbol}`;
	} else {
		return `${quantity} ${quantity > 1 ? 'lots' : 'lot'} x ${lotSize}${displaySymbol}`;
	}
};

function isLotSizeApplicable(exchange) {
	return ['NSE_EQ', 'BSE_EQ', 'NSE_FO'].includes(exchange);
}

function getValidityValue(val, exchange) {
	const isBseExchange = orderScripService.isBseExchange(exchange);
	const isBseExchangeAndDay = isBseExchange && val === VALIDITY_TYPE.DAY.value;
	let validity = val;
	if (isBseExchangeAndDay) {
		validity = VALIDITY_TYPE.EOTODY.value;
	}

	return validity;
}

function shouldHideDisclosedQuantity(exchange) {
	return exchange === EQUITY_MARKET_SEGMENTS.NSE_FO;
}

function shouldHideAmoSection(productType) {
	return [PRODUCT_TYPES_MAPPER.btst.id, PRODUCT_TYPES_MAPPER.mtf.id].includes(productType);
}

function calculatePayloadMarketPrice(feedMessage = {}, limitPrice = '', orderType = '') {
	const isMarketOrStopLossMarketOrderType = isMarketOrStopLossMarketOrder(orderType);

	const lastTradePrice = feedMessage?.last_trade_price;
	const bidPrice = feedMessage?.market_depth_buy?.[0]?.price;
	const askPrice = feedMessage?.market_depth_sell?.[0]?.price;
	const divider = feedMessage?.divider;

	const maxOrderPrice =
		numberValidityService.convertPaisaToRupees(max([lastTradePrice, bidPrice, askPrice]), divider) || 0;

	const orderPrice = isMarketOrStopLossMarketOrderType ? maxOrderPrice : limitPrice;

	return orderPrice;
}

const isStopLossMarketAndDisabled = (instrumentName, orderType) => {
	const isOptionScrip = orderScripService.isOptionScrip(instrumentName);
	const isStopLossMarket = orderFormService.isStopLossMarketOrder(orderType);
	return isStopLossMarket && isOptionScrip;
};

const isMarketAndDisabled = (instrumentName, orderType, isAmo) => {
	const isOptionScrip = orderScripService.isOptionScrip(instrumentName);
	const isMarketOrder = orderFormService.isMarketOrder(orderType);
	return isMarketOrder && isOptionScrip && isAmo;
};

const orderFormService = {
	checkShouldShowStoploss,
	checkShouldShowDisclosedQuantity,
	handleIncreamentDecrementAction,
	checkAmoDisabled,
	isDecimalNumber,
	getApplicableProductTypesForUser,
	getEditOrderDisclosedQty,
	isMarketOrStopLossMarketOrder,
	isLimitOrStopLossOrder,
	orderFormRedirection,
	getMarginMarketFlag,
	isEdisApplicable,
	getCurrencySymbolForCDS,
	isEdisAndHoldingsApplicable,
	getOrderSwiperTitle,
	getDefaultTab,
	isDeliveryOrIntradayOrder,
	showAvailableQuantity,
	isOrderTypeStoplossLimit,
	isOrderTypeStoploss,
	getFormattedLotText,
	isLotSizeApplicable,
	getValidityValue,
	shouldHideDisclosedQuantity,
	shouldHideAmoSection,
	calculatePayloadMarketPrice,
	isDeliverySellOrder,
	isSellTransaction,
	isIocOrder,
	isIocDisabled,
	isEquityOrderByApiSegment,
	isDeliveryOrder,
	isEquityOrderByExchange,
	isEquityOrderBySegmentID,
	isMarketOrder,
	isIntradayOrder,
	getDefaultOrPreviousOrderType,
	isStopLossMarketOrder,
	isStopLossMarketAndDisabled,
	isMarketAndDisabled,
};

export default orderFormService;
